import { useCallback, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  api,
  BulkUpdateDocumentDto,
  DocumentDto,
  QueryKey,
  UpdateDocumentDto,
  useBulkUpdateDocuments,
} from '@sib/api';
import { createUseAppTranslation } from '@sib/i18n';
import { useNotification } from '@sib/notifications';
import { Undo } from '@sib/ui/icons';

export type SharedBulkEditPayload = Omit<BulkUpdateDocumentDto, 'documentIds'>;

interface BulkEditSnapshotState<TSnapshot> {
  documentIds: string[];
  originalById: Map<string, TSnapshot>;
  organizationIdByDocumentId?: Map<string, string>;
}

interface BulkEditNotificationContext {
  documentIds: string[];
  showDefaultNotification: () => void;
}

export interface UseDocumentBulkEditWithUndoOptions<TSnapshot> {
  organizationId?: string;
  getDocumentOrganizationId?: (documentId: string) => string | undefined;
  updateDocument?: (
    documentId: string,
    organizationId: string,
    data: UpdateDocumentDto
  ) => Promise<unknown>;
  bulkUpdateDocuments?: (data: BulkUpdateDocumentDto) => Promise<unknown>;
  isBulkUpdatePending?: boolean;
  documents: DocumentDto[];
  createSnapshot: (
    documentIds: string[],
    documents: DocumentDto[]
  ) => Map<string, TSnapshot>;
  buildUndoUpdate: (snapshot: TSnapshot) => UpdateDocumentDto;
  notificationKeyPrefix?: string;
  onSuccess?: (context: BulkEditNotificationContext) => void;
  onError?: (context: BulkEditNotificationContext) => void;
}

const useDocumentsTranslation = createUseAppTranslation('documents');

export const useDocumentBulkEditWithUndo = <TSnapshot,>({
  organizationId,
  getDocumentOrganizationId,
  documents,
  createSnapshot,
  buildUndoUpdate,
  notificationKeyPrefix = 'bulk-edit',
  updateDocument,
  bulkUpdateDocuments,
  isBulkUpdatePending,
  onSuccess,
  onError,
}: UseDocumentBulkEditWithUndoOptions<TSnapshot>) => {
  const { t } = useDocumentsTranslation();
  const { open: openNotification, close: closeNotification } =
    useNotification();
  const queryClient = useQueryClient();

  const snapshotRef = useRef<BulkEditSnapshotState<TSnapshot> | null>(null);
  const [isMultiOrgUpdating, setIsMultiOrgUpdating] = useState(false);

  const isMultiOrg = !!getDocumentOrganizationId;

  const {
    mutateAsync: defaultBulkUpdateDocuments,
    isPending: isSingleOrgUpdating,
  } = useBulkUpdateDocuments({
    organizationId: organizationId ?? '',
  });

  const updateSingleDocument = useMemo(
    () =>
      updateDocument ??
      ((
        documentId: string,
        documentOrganizationId: string,
        data: UpdateDocumentDto
      ) =>
        api.organization.documentControllerUpdate(
          documentId,
          documentOrganizationId,
          data
        )),
    [updateDocument]
  );
  const bulkUpdateDocumentsMutation = useMemo(
    () => bulkUpdateDocuments ?? defaultBulkUpdateDocuments,
    [bulkUpdateDocuments, defaultBulkUpdateDocuments]
  );

  const isBulkUpdating = isMultiOrg
    ? isMultiOrgUpdating
    : (isBulkUpdatePending ?? isSingleOrgUpdating);

  const handleUndo = useCallback(async () => {
    if (!snapshotRef.current) return;

    const { documentIds, originalById, organizationIdByDocumentId } =
      snapshotRef.current;

    try {
      await Promise.all(
        documentIds.map(docId => {
          const original = originalById.get(docId);
          const docOrgId =
            organizationIdByDocumentId?.get(docId) ?? organizationId;
          if (!original || !docOrgId) return Promise.resolve();

          return updateSingleDocument(
            docId,
            docOrgId,
            buildUndoUpdate(original)
          );
        })
      );

      const showUndoSuccessNotification = () =>
        openNotification({
          message: t('components.bulkEditModal.undoSuccess'),
          type: 'success',
        });

      if (onSuccess) {
        onSuccess({
          documentIds,
          showDefaultNotification: showUndoSuccessNotification,
        });
      } else {
        showUndoSuccessNotification();
      }

      snapshotRef.current = null;
    } catch {
      const showUndoErrorNotification = () =>
        openNotification({
          message: t('components.bulkEditModal.undoError'),
          type: 'error',
        });

      if (onError) {
        onError({
          documentIds,
          showDefaultNotification: showUndoErrorNotification,
        });
      } else {
        showUndoErrorNotification();
      }
    }
  }, [
    organizationId,
    openNotification,
    t,
    onSuccess,
    buildUndoUpdate,
    onError,
    updateSingleDocument,
  ]);

  const handleMultiOrgBulkEdit = useCallback(
    async (
      documentIds: string[],
      bulkEditData: SharedBulkEditPayload
    ): Promise<boolean> => {
      const groupedByOrg = documentIds.reduce<Map<string, string[]>>(
        (acc, id) => {
          const orgId = getDocumentOrganizationId!(id);
          if (!orgId) return acc;

          const group = acc.get(orgId) ?? [];
          group.push(id);
          acc.set(orgId, group);

          return acc;
        },
        new Map()
      );

      const validDocumentIds = Array.from(groupedByOrg.values()).flat();

      if (validDocumentIds.length === 0) return false;

      const organizationIdByDocumentId = validDocumentIds.reduce<
        Map<string, string>
      >((acc, id) => {
        const orgId = getDocumentOrganizationId!(id);
        if (orgId) acc.set(id, orgId);

        return acc;
      }, new Map());

      snapshotRef.current = {
        documentIds: validDocumentIds,
        originalById: createSnapshot(validDocumentIds, documents),
        organizationIdByDocumentId,
      };

      setIsMultiOrgUpdating(true);

      try {
        await Promise.all(
          Array.from(groupedByOrg.entries()).map(([orgId, orgDocIds]) =>
            api.organization.documentControllerBulkUpdate(orgId, {
              ...bulkEditData,
              documentIds: orgDocIds,
            })
          )
        );

        queryClient.invalidateQueries({
          queryKey: [QueryKey.Resource.Documents],
        });
        queryClient.invalidateQueries({
          queryKey: [QueryKey.Resource.DocumentVendorSelectList],
        });
        Array.from(groupedByOrg.keys()).forEach(orgId => {
          queryClient.invalidateQueries({
            queryKey: [QueryKey.Resource.DocumentColumnMaxValues, orgId],
          });
        });

        return true;
      } catch {
        snapshotRef.current = null;

        return false;
      } finally {
        setIsMultiOrgUpdating(false);
      }
    },
    [createSnapshot, documents, getDocumentOrganizationId, queryClient]
  );

  const handleBulkEdit = useCallback(
    async (
      documentIds: string[],
      bulkEditData: SharedBulkEditPayload
    ): Promise<boolean> => {
      if (documentIds.length === 0) return false;

      const showBulkErrorNotification = () =>
        openNotification({
          message: t('components.bulkEditModal.error'),
          type: 'error',
        });

      const handleError = () => {
        if (onError) {
          onError({
            documentIds,
            showDefaultNotification: showBulkErrorNotification,
          });
        } else {
          showBulkErrorNotification();
        }
      };

      if (isMultiOrg) {
        const success = await handleMultiOrgBulkEdit(documentIds, bulkEditData);

        if (!success) {
          handleError();

          return false;
        }
      } else {
        const originalById = createSnapshot(documentIds, documents);

        snapshotRef.current = { documentIds, originalById };

        try {
          await bulkUpdateDocumentsMutation({ documentIds, ...bulkEditData });
        } catch {
          snapshotRef.current = null;
          handleError();

          return false;
        }
      }

      const notificationId = `${notificationKeyPrefix}-${Date.now()}`;
      const showBulkSuccessNotification = () =>
        openNotification({
          key: notificationId,
          message: t('components.bulkEditModal.success', {
            count: documentIds.length,
          }),
          type: 'success',
          duration: 10000,
          action: {
            label: t('undo'),
            icon: <Undo className="size-4" />,
            onClick: () => {
              closeNotification(notificationId);
              void handleUndo();
            },
          },
        });

      if (onSuccess) {
        onSuccess({
          documentIds,
          showDefaultNotification: showBulkSuccessNotification,
        });
      } else {
        showBulkSuccessNotification();
      }

      return true;
    },
    [
      isMultiOrg,
      handleMultiOrgBulkEdit,
      createSnapshot,
      documents,
      bulkUpdateDocumentsMutation,
      openNotification,
      closeNotification,
      t,
      handleUndo,
      onSuccess,
      onError,
      notificationKeyPrefix,
    ]
  );

  return {
    handleBulkEdit,
    isBulkUpdating,
  };
};
